import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from '@/contexts/LanguageContext';

interface ProgressIndicatorProps {
  currentStep: number;
}

export const ProgressIndicator = ({ currentStep }: ProgressIndicatorProps) => {
  const isMobile = useIsMobile();
  const { t } = useLanguage();

  const steps = [
    { number: 1, title: t('registration.progressSteps.participant'), description: t('registration.progressSteps.participantDesc') },
    { number: 2, title: t('registration.progressSteps.payment'), description: t('registration.progressSteps.paymentDesc') },
    { number: 3, title: t('registration.progressSteps.confirmation'), description: t('registration.progressSteps.confirmationDesc') },
  ];
  
  return (
    <div className="w-full">
      <div className="flex items-center justify-center">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center">
            <div className="flex flex-col items-center">
              {/* Step Circle */}
              <div className="flex items-center justify-center">
                <div
                  className={cn(
                    `${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full flex items-center justify-center ${isMobile ? 'text-xs' : 'text-sm'} font-semibold border-2 transition-all duration-200`,
                    currentStep > step.number
                      ? "bg-green-500 border-green-500 text-white"
                      : currentStep === step.number
                      ? "bg-[#3D6B4F] border-[#3D6B4F] text-white"
                      : "bg-white border-gray-300 text-gray-400"
                  )}
                >
                  {currentStep > step.number ? (
                    <Check className={`${isMobile ? 'w-3 h-3' : 'w-5 h-5'}`} />
                  ) : (
                    step.number
                  )}
                </div>
              </div>

              {/* Step Label */}
              <div className={`${isMobile ? 'mt-2' : 'mt-3'} text-center`}>
                <p
                  className={cn(
                    `${isMobile ? 'text-xs' : 'text-sm'} font-medium transition-colors duration-200`,
                    currentStep >= step.number
                      ? "text-gray-900"
                      : "text-gray-400"
                  )}
                >
                  {step.title}
                </p>
                {!isMobile && (
                  <p
                    className={cn(
                      "text-xs transition-colors duration-200",
                      currentStep >= step.number
                        ? "text-gray-600"
                        : "text-gray-400"
                    )}
                  >
                    {step.description}
                  </p>
                )}
              </div>
            </div>

            {/* Connector Line */}
            {index < steps.length - 1 && (
              <div className={`${isMobile ? 'w-16 mx-4' : 'w-24 mx-6'} flex items-center`}>
                <div
                  className={cn(
                    "w-full h-0.5 transition-all duration-200",
                    currentStep > step.number
                      ? "bg-green-500"
                      : "bg-gray-300"
                  )}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
